import React, { useEffect, useMemo, useState } from "react";
import {
  previewXpAdjustment,
  XP_DAILY_ACTION_LIMIT,
  XP_DAILY_POINTS_LIMIT,
  XP_LARGE_CONFIRM_THRESHOLD,
  XP_MAX_ADJUSTMENT_POINTS,
  xpAdminQuotaForToday,
  xpLevelInfoForProfile,
  xpPointsForProfile,
  xpRewardForProfile,
} from "./chatService";
import { avatarColor, avatarInitial } from "./avatar";

export default function XpPointsModal({
  me,
  profilesByUid,
  onClose,
  onAdjustXpPoints,
}) {
  const [busyUid, setBusyUid] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUid, setSelectedUid] = useState("");
  const [mode, setMode] = useState("grant");
  const [amount, setAmount] = useState("10");
  const [reason, setReason] = useState("");

  const adminQuota = xpAdminQuotaForToday(me || {});
  const actionsLeft = Math.max(0, XP_DAILY_ACTION_LIMIT - adminQuota.actionsUsed);
  const pointsLeft = Math.max(0, XP_DAILY_POINTS_LIMIT - adminQuota.pointsUsed);

  const users = useMemo(() => {
    return Object.values(profilesByUid || {})
      .filter((profile) => profile.uid && profile.uid !== me?.uid)
      .sort((a, b) => String(a.displayName || "").localeCompare(String(b.displayName || "")));
  }, [me?.uid, profilesByUid]);

  const filteredUsers = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return users;
    return users.filter((profile) => {
      const haystack = [profile.displayName, profile.loginName, profile.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery, users]);

  useEffect(() => {
    if (filteredUsers.length === 0) {
      setSelectedUid("");
      return;
    }
    if (!filteredUsers.some((profile) => profile.uid === selectedUid)) {
      setSelectedUid(filteredUsers[0].uid);
    }
  }, [filteredUsers, selectedUid]);

  const selectedProfile = filteredUsers.find((profile) => profile.uid === selectedUid) || filteredUsers[0] || null;
  const selectedXpPoints = xpPointsForProfile(selectedProfile || {});
  const selectedLevel = xpLevelInfoForProfile(selectedProfile || {});
  const selectedReward = xpRewardForProfile(selectedProfile || {});
  const preview = previewXpAdjustment({
    currentPoints: selectedXpPoints,
    mode,
    amount: Number(amount || 0),
  });

  function setQuickAmount(value) {
    setAmount(String(value));
  }

  async function submitAdjustment() {
    if (!selectedProfile?.uid || typeof onAdjustXpPoints !== "function") return;
    const trimmedReason = String(reason || "").trim();
    if (trimmedReason.length < 3) {
      setError("Add a short reason so users know why their XP changed.");
      return;
    }
    if (!preview.absoluteDelta) {
      setError("That change would not adjust this user's XP.");
      return;
    }
    if (preview.absoluteDelta > XP_MAX_ADJUSTMENT_POINTS) {
      setError(`You can change at most ${XP_MAX_ADJUSTMENT_POINTS} XP in one action.`);
      return;
    }
    if (actionsLeft <= 0 || pointsLeft < preview.absoluteDelta) {
      setError("Daily XP admin limit reached.");
      return;
    }
    if (preview.absoluteDelta >= XP_LARGE_CONFIRM_THRESHOLD || mode === "set") {
      const confirmed = window.confirm(
        `${mode === "set" ? "Set" : mode === "remove" ? "Remove" : "Give"} ${preview.absoluteDelta} XP ${mode === "set" ? "difference" : ""} for ${selectedProfile.displayName || "this user"}?\n\nReason: ${trimmedReason}`
      );
      if (!confirmed) return;
    }

    setBusyUid(selectedProfile.uid);
    setError("");
    setNotice("");
    try {
      const result = await onAdjustXpPoints({
        targetUid: selectedProfile.uid,
        amount: Number(amount || 0),
        mode,
        reason: trimmedReason,
      });
      setNotice(
        `${selectedProfile.displayName || "User"} now has ${result?.nextXpPoints ?? preview.nextXpPoints} XP.`
      );
      if (mode !== "set") setAmount("10");
      setReason("");
    } catch (err) {
      setError(err?.message || "Couldn't grant XP points.");
    } finally {
      setBusyUid("");
    }
  }

  const quickValues = mode === "set"
    ? [0, 100, 250, 500, 1000]
    : [5, 10, 25, 100, 250];

  const submitDisabled = !selectedProfile
    || !!busyUid
    || actionsLeft <= 0
    || pointsLeft < preview.absoluteDelta
    || preview.absoluteDelta < 1
    || preview.absoluteDelta > XP_MAX_ADJUSTMENT_POINTS;

  return (
    <div className="chat-modal-bg" onClick={busyUid ? undefined : onClose}>
      <div className="chat-modal chat-xp-modal" onClick={(event) => event.stopPropagation()}>
        <h2>XP points admin</h2>
        <p className="chat-muted">Grant, remove, or correct XP with search, reasons, and safety limits.</p>

        <div className="chat-xp-guardrail-row">
          <span>{actionsLeft} actions left today</span>
          <span>{pointsLeft} points left today</span>
          <span>{XP_MAX_ADJUSTMENT_POINTS} max per action</span>
        </div>

        <input
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="chat-modal-search"
          placeholder="Search username or email"
        />

        <div className="chat-xp-modal-shell">
          <div className="chat-modal-userlist chat-xp-userlist">
            {filteredUsers.length === 0 ? (
              <div className="chat-muted">No users match that search.</div>
            ) : (
              filteredUsers.map((profile) => {
                const xpLevel = xpLevelInfoForProfile(profile);
                const xpReward = xpRewardForProfile(profile);
                return (
                  <button
                    key={profile.uid}
                    type="button"
                    className={"chat-xp-user-option" + (profile.uid === selectedUid ? " active" : "")}
                    onClick={() => {
                      setSelectedUid(profile.uid);
                      setNotice("");
                      setError("");
                    }}
                  >
                    <div className="chat-avatar sm" style={{ background: avatarColor(profile.uid) }}>
                      {avatarInitial(profile.displayName)}
                    </div>
                    <div className="chat-xp-user-meta">
                      <strong>{profile.displayName || "User"}</strong>
                      <span>{xpPointsForProfile(profile)} XP • L{xpLevel.level}</span>
                    </div>
                    <span className={`chat-xp-badge ${xpReward.key}`}>{xpReward.label}</span>
                  </button>
                );
              })
            )}
          </div>

          {selectedProfile ? (
            <div className="chat-xp-editor">
              <div className="chat-xp-selected-card">
                <div className="chat-avatar lg" style={{ background: avatarColor(selectedProfile.uid) }}>
                  {avatarInitial(selectedProfile.displayName)}
                </div>
                <div className="chat-xp-selected-meta">
                  <h3>{selectedProfile.displayName || "User"}</h3>
                  <span>@{selectedProfile.loginName || "chat"}</span>
                  <div className="chat-xp-selected-stats">
                    <strong>{selectedXpPoints} XP</strong>
                    <span>Level {selectedLevel.level}</span>
                    <span className={`chat-xp-badge ${selectedReward.key}`}>{selectedReward.label}</span>
                  </div>
                </div>
              </div>

              <div className="chat-xp-form-grid">
                <label>
                  <span>Action</span>
                  <select value={mode} onChange={(event) => setMode(event.target.value)} className="chat-xp-select">
                    <option value="grant">Give XP</option>
                    <option value="remove">Remove XP</option>
                    <option value="set">Set exact XP</option>
                  </select>
                </label>
                <label>
                  <span>{mode === "set" ? "Exact XP" : "Amount"}</span>
                  <input
                    type="number"
                    min={mode === "set" ? "0" : "1"}
                    step="1"
                    className="chat-xp-input"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    disabled={!!busyUid}
                  />
                </label>
              </div>

              <div className="chat-xp-quick-row">
                {quickValues.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="chat-msg-action-btn"
                    disabled={!!busyUid}
                    onClick={() => setQuickAmount(value)}
                  >
                    {mode === "set" ? `${value} total` : `${value} XP`}
                  </button>
                ))}
              </div>

              <label className="chat-xp-reason-field">
                <span>Reason</span>
                <input
                  type="text"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className="chat-modal-search"
                  maxLength={140}
                  placeholder="Why are you changing this user's XP?"
                  disabled={!!busyUid}
                />
              </label>

              <div className="chat-xp-preview-card">
                <div>
                  <span>Current</span>
                  <strong>{preview.currentXpPoints} XP</strong>
                </div>
                <div>
                  <span>Change</span>
                  <strong>{preview.delta > 0 ? `+${preview.delta}` : preview.delta} XP</strong>
                </div>
                <div>
                  <span>Next</span>
                  <strong>{preview.nextXpPoints} XP</strong>
                </div>
              </div>

              <button
                type="button"
                className="chat-btn-primary chat-panel-wide-btn"
                disabled={submitDisabled}
                onClick={submitAdjustment}
              >
                {busyUid
                  ? "Saving..."
                  : mode === "remove"
                    ? "Remove XP"
                    : mode === "set"
                      ? "Set exact XP"
                      : "Give XP"}
              </button>

              {notice && <div className="chat-zk-card-note">{notice}</div>}
            </div>
          ) : (
            <div className="chat-muted">Pick a user to adjust their XP.</div>
          )}
        </div>

        {error && <div className="chat-auth-error">{error}</div>}

        <div className="chat-modal-footer">
          <button type="button" className="chat-btn-link" onClick={onClose} disabled={!!busyUid}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}